import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UploadImageDialog from '../../../../core/dialogs/input/components/UploadImageDialog';
import { useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { formatName } from '../../../../utils/helper';

const DialogFooter = observer(({ onSave, store }: any) => {
    return (
        <div className="flex justify-content-between align-items-center">
            <Button
                label="Frage hinzufügen"
                icon="pi pi-plus"
                disabled={store.isNewTraining || store.isLoading}
                onClick={() => store.onAddQuestion()}
            />
            <div>
                <Button
                    label={store.buttonLabel}
                    icon="pi pi-check"
                    className="p-button-success"
                    disabled={!store.isValid || store.isLoading}
                    onClick={() => {
                        onSave();
                    }}
                />
            </div>
        </div>
    );
});

const TrainingContent = observer(({ store }: { store: any }) => {
    const fileUploadRef = useRef<FileUpload>(null);

    const contentTypeOptions = [
        { label: 'Video (URL)', value: 'video' },
        { label: 'PDF-Datei', value: 'pdf' },
    ];

    const handleContentTypeChange = (e: any) => {
        store.trainingContentType = e.value;
        if (e.value !== 'pdf' && fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    const handleVideoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        store.trainingVideo = e.target.value;
    };

    const handlePdfSelect = async (event: any) => {
        const file = event.files[0];

        if (file && file.type === 'application/pdf') {
            const success = await store.uploadPdf(file);
            if (success) {
                if (fileUploadRef.current) {
                    fileUploadRef.current.clear();
                }
            } else {
                console.error('PDF upload failed');
            }
        } else {
            console.error('Invalid file type or no file selected');
        }
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Inhalts-Typ</label>
            <Dropdown
                value={store.trainingContentType}
                options={contentTypeOptions}
                onChange={handleContentTypeChange}
                placeholder="Wählen Sie den Inhalts-Typ"
                style={{ width: '100%', marginBottom: '1rem' }}
            />

            {store.trainingContentType === 'video' && (
                <div>
                    <label
                        htmlFor="trainingVideo"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Video-Link
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <InputText
                            id="videoUrl"
                            placeholder="Fügen Sie einen Video-Link ein"
                            value={store.trainingVideo}
                            onChange={handleVideoInputChange}
                            style={{ flex: 1 }}
                            className={store.isTrainingVideoValid ? '' : 'p-invalid'}
                        />
                        <Button
                            icon="pi pi-external-link"
                            className="p-button-success"
                            tooltip="Link in neuem Tab öffnen"
                            disabled={!store.isTrainingVideoValid}
                            onClick={() => store.openVideoUrl()}
                        />
                    </div>
                </div>
            )}

            {store.trainingContentType === 'pdf' && (
                <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                        PDF-Datei auswählen
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileUpload
                            ref={fileUploadRef}
                            mode="basic"
                            name="pdf"
                            accept=".pdf"
                            maxFileSize={50000000} // 50MB
                            onSelect={handlePdfSelect}
                            chooseLabel={store.trainingContentFile ? 'PDF ändern' : 'PDF auswählen'}
                            className={
                                store.trainingContentFile ? 'p-fileupload-choose-selected' : ''
                            }
                            style={{ flex: 1 }}
                        />
                        {store.trainingContentFile && (
                            <>
                                <Button
                                    icon="pi pi-external-link"
                                    className="p-button-success"
                                    tooltip="PDF in neuem Tab öffnen"
                                    onClick={() => store.openPdfUrl()}
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-danger"
                                    tooltip="PDF entfernen"
                                    onClick={() => {
                                        store.trainingContentFile = null;
                                        if (fileUploadRef.current) {
                                            fileUploadRef.current.clear();
                                        }
                                    }}
                                />
                            </>
                        )}
                    </div>
                    {store.trainingContentFile && (
                        <div style={{ marginTop: '0.5rem', color: 'green' }}>
                            <i
                                className="pi pi-check-circle"
                                style={{ marginRight: '0.5rem' }}
                            ></i>
                            PDF erfolgreich hochgeladen
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

const TrainingName = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 80) {
            store.trainingName = e.target.value;
        }
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="trainingName"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Schulung-Name
            </label>
            <InputText
                id="trainingName"
                placeholder="Schulungsname eingeben"
                value={store.trainingName}
                onChange={handleInputChange}
                style={{ width: '100%' }}
                data-pr-tooltip="Der Schulungsname muss mindestens 3 Zeichen lang sein."
                data-pr-position="top"
                data-pr-disabled={store.isTrainingNameValid}
                invalid={!store.isTrainingNameValid}
            />
            <Tooltip target="#trainingName" />
        </div>
    );
});

const TrainingDescription = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        store.trainingDescription = e.target.value;
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="trainingDescription"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Schulung-Beschreibung
            </label>
            <InputTextarea
                id="trainingDescription"
                placeholder="Beschreibug eingeben"
                value={store.trainingDescription}
                onChange={handleInputChange}
                rows={5}
                cols={30}
                style={{ width: '100%' }}
            />
        </div>
    );
});

const TrainingQuestionEditorFooter = observer(({ onSave, store }: any) => {
    return (
        <div className="flex justify-content-between align-items-center">
            <Button
                label="Antwort hinzufügen"
                icon="pi pi-plus"
                disabled={store.editor?.maximumAnswersReached} // Disable if maximum answers reached
                onClick={() => store.editor?.addAnswer()} // Add a new answer to the question
            />
            <div>
                <Button
                    label="Speichern + Schließen"
                    icon="pi pi-save"
                    onClick={onSave}
                    disabled={!store.editor?.isValid} // Disable if the question is not valid
                    autoFocus
                />
            </div>
        </div>
    );
});

const TrainingQuestionEditor = observer(({ store }: { store: any }) => {
    const handleClose = () => {
        store.closeQuestionEditor(); // Close the dialog by setting the editor to null
    };

    return (
        <Dialog
            visible={!!store.editor} // Show dialog only if editor is available
            header="Frage bearbeiten"
            footer={() => (
                <TrainingQuestionEditorFooter
                    onSave={async () => {
                        store.onSaveQuestion().then((success: boolean) => {
                            if (success) {
                                handleClose(); // Close the dialog after saving
                                store.fetchTraining(); // Fetch the training data again to refresh the list
                            }
                        });
                    }}
                    onArchived={() => {}}
                    store={store}
                />
            )}
            onHide={handleClose}
            style={{ width: '50rem' }}
        >
            {store.editor && (
                <div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label
                            htmlFor="questionText"
                            style={{ display: 'block', marginBottom: '0.5rem' }}
                        >
                            Fragetext
                        </label>
                        <InputText
                            id="questionText"
                            value={store.editor.question?.text || ''}
                            onChange={e => {
                                if (store.editor?.question) {
                                    store.editor.question.updateText(e.target.value);
                                }
                            }}
                            placeholder="Fragetext eingeben"
                            style={{ width: '100%' }}
                            data-pr-tooltip="Die Frage muss mindestens drei Zeichen lang sein."
                            data-pr-position="top"
                            data-pr-disabled={store.editor.question.isValid}
                            invalid={!store.editor.question?.isValid}
                        />
                        <Tooltip target="#questionText" />
                    </div>
                    <div>
                        <h4>Antworten</h4>
                        {store.editor.answers?.map((answer: any, index: number) => (
                            <div
                                key={`answer-${answer.id || index}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '10px',
                                }}
                            >
                                <InputText
                                    id={`answer-text-${answer.id || index}`}
                                    value={answer.text}
                                    onChange={e => {
                                        answer.updateText(e.target.value);
                                    }}
                                    data-pr-tooltip="Die Antwort muss mindestens drei Zeichen lang sein."
                                    data-pr-position="top"
                                    data-pr-disabled={answer.isValid}
                                    invalid={!answer.isValid}
                                    placeholder={`Antwort ${index + 1}`}
                                    className="w-full"
                                />
                                <Tooltip target={`#answer-text-${answer.id || index}`} />
                                <Button
                                    icon={answer.isCorrect ? 'pi pi-check-circle' : 'pi pi-circle'}
                                    className={`p-button-sm ${
                                        answer.isCorrect ? 'p-button-success' : 'p-button-secondary'
                                    }`}
                                    onClick={() => answer.swtichCorrect()}
                                    tooltip={
                                        answer.isCorrect
                                            ? 'Richtige Antwort'
                                            : 'Als richtig markieren'
                                    }
                                />
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-sm p-button-danger"
                                    onClick={() => store.editor?.removeAnswer(answer.id)}
                                    tooltip="Antwort löschen"
                                />
                            </div>
                        ))}
                        {store.editor.answers?.length < 2 && (
                            <p style={{ color: 'red', fontStyle: 'italic' }}>
                                Mindestens zwei Antworten erforderlich!
                            </p>
                        )}
                        {!(store.editor.answers || []).some((answer: any) => answer.isCorrect) && (
                            <p style={{ color: 'red', fontStyle: 'italic' }}>
                                Mindestens eine Antwort muss richtig sein!
                            </p>
                        )}
                    </div>
                </div>
            )}
        </Dialog>
    );
});

const SortableQuestion = ({
    id,
    question,
    isActive,
    onClick,
    onEditQuestion,
    onDeleteQuestion,
}: any) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: '20px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '15px',
        backgroundColor: isActive ? '#f9f9f9' : '#fff',
        cursor: 'grab',
        display: 'flex',
        flexDirection: 'column' as 'column', // Top-down layout
        gap: '15px',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Button
                        icon={isActive ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                        className="p-button-text p-button-sm"
                        onMouseDown={e => e.stopPropagation()}
                        onPointerDown={e => e.stopPropagation()} // Prevent drag-and-drop from interfering
                        onClick={e => {
                            e.stopPropagation(); // Prevent drag listeners from interfering
                            onClick();
                        }}
                    />
                    <div style={{ flex: 1 }}>{question.text}</div>
                </div>
            </div>
            {isActive && (
                <div style={{ textAlign: 'left' }}>
                    <h4>Antworten</h4>
                    {question.answers.map((answer: any, index: number) => (
                        <div
                            key={`question-${question.id}-answer-${answer.id || index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '10px',
                            }}
                        >
                            <InputText
                                value={answer.text}
                                placeholder={`Antwort ${index + 1}`}
                                className="w-full"
                                disabled={true}
                            />
                            <Button
                                icon={answer.isCorrect ? 'pi pi-check-circle' : 'pi pi-circle'}
                                className={`p-button-sm ${
                                    answer.isCorrect ? 'p-button-success' : 'p-button-secondary'
                                }`}
                                disabled={true}
                                tooltip={
                                    answer.isCorrect ? 'Richtige Antwort' : 'Als richtig markieren'
                                }
                            />
                        </div>
                    ))}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            icon="pi pi-pencil"
                            label="Frage bearbeiten"
                            className="p-button-sm p-button-secondary"
                            onMouseDown={e => e.stopPropagation()}
                            onPointerDown={e => e.stopPropagation()}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                onEditQuestion();
                            }}
                        />
                        <Button
                            icon="pi pi-trash"
                            label="Frage löschen"
                            className="p-button-sm p-button-danger"
                            onMouseDown={e => e.stopPropagation()}
                            onPointerDown={e => e.stopPropagation()}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                onDeleteQuestion();
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const TrainingQuestionCatalog = observer(({ store, sdk }: { store: any; sdk: any }) => {
    const onDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            store.changeOrderQuestion(active.id, over.id); // Update the order of questions in the store
        }
    };

    return (
        <div className="p-4">
            <DndContext
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={store?.allQuestionOrder || []}
                    strategy={verticalListSortingStrategy}
                >
                    {(store.allQuestions || []).map((q: any) => (
                        <SortableQuestion
                            key={q.id}
                            id={q.id}
                            question={q}
                            isActive={store.menuActiveQuestion === q.id}
                            onClick={() => store.setMenuActiveQuestion(q.id)}
                            onEditQuestion={() => store.openQuestionEditor(q.id)}
                            onDeleteQuestion={async () => {
                                const result = await sdk.getDialogDelete({
                                    title: 'Möchten Sie die Frage wirklich löschen?',
                                    message: 'Bitte geben Sie "löschen" ein, um zu bestätigen.',
                                    searchWord: 'löschen',
                                    onConfirm: async () => store.deleteQuestion(q.id),
                                });

                                if (result.confirmed) {
                                    sdk.showSuccessToast('Frage erfolgreich gelöscht');
                                } else if (result.error) {
                                    sdk.showErrorToast(
                                        result.error || 'Fehler beim Löschen der Frage'
                                    );
                                }
                            }}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <TrainingQuestionEditor store={store} />
        </div>
    );
});

const TrainingEmployeeResults = observer(({ store, sdk }: { store: any; sdk: any }) => {
    const statusBodyTemplate = (rowData: any) => {
        let value: string;
        let severity: 'success' | 'danger' | 'warning' | 'info' | 'secondary' | 'contrast';

        if (rowData.training_status === 'completed') {
            value = 'Bestanden';
            severity = 'success';
        } else if (rowData.training_status === 'failed') {
            value = 'Nicht bestanden';
            severity = 'danger';
        } else {
            value = 'Offen';
            severity = 'warning';
        }

        return (
            <Tag
                value={value}
                severity={severity}
            />
        );
    };

    const dateBodyTemplate = (rowData: any) => {
        if (rowData.training_session_date) {
            return new Date(rowData.training_session_date).toLocaleDateString('de-DE');
        }
        return '-';
    };

    const nameBodyTemplate = (rowData: any) => {
        return formatName(rowData.firstname, rowData.lastname);
    };

    const onSort = (event: any) => {
        const field = event.sortField;
        const order = event.sortOrder === 1 ? 'asc' : 'desc';

        // Map frontend field names to backend field names
        let sortField = field;
        if (field === 'name') {
            sortField = 'lastname';
        } else if (field === 'training_session_date') {
            sortField = 'training_session_date';
        } else if (field === 'training_status') {
            sortField = 'status_priority';
        }

        store.onEmployeeResultsSort(sortField, order);
    };

    const onPageChange = (event: any) => {
        const newPage = event.page + 1; // PrimeReact uses 0-based pages
        const newPageSize = event.rows;

        store.onEmployeeResultsPageChange(newPage, newPageSize);
    };

    return (
        <div className="p-4">
            <DataTable
                value={store.employeeResults || []}
                loading={store.isLoadingResults}
                emptyMessage={'Keine Reinigungskräfte gefunden.'}
                data-testid={'employee-results-table'}
                paginator
                rows={store.employee_results_page_size || 10}
                first={
                    (store.employee_results_current_page - 1) *
                    (store.employee_results_page_size || 10)
                }
                totalRecords={store.employee_results_pagination?.total || 0}
                onPage={onPageChange}
                lazy
                sortMode="single"
                onSort={onSort}
                removableSort={false}
                sortField={
                    store.employee_results_sort_field === 'lastname'
                        ? 'name'
                        : store.employee_results_sort_field === 'training_session_date'
                          ? 'training_session_date'
                          : store.employee_results_sort_field === 'status_priority' ||
                              store.employee_results_sort_field === 'has_passed'
                            ? 'training_status'
                            : store.employee_results_sort_field
                }
                sortOrder={store.employee_results_sort_order === 'asc' ? 1 : -1}
                rowsPerPageOptions={[10, 25, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Zeige {first} bis {last} von {totalRecords} Einträgen"
            >
                <Column
                    field="name"
                    header="Name"
                    body={nameBodyTemplate}
                    sortable
                />
                <Column
                    field="training_status"
                    header="Status"
                    body={statusBodyTemplate}
                    sortable
                />
                <Column
                    field="training_session_date"
                    header="Datum"
                    body={dateBodyTemplate}
                    sortable
                />
            </DataTable>
        </div>
    );
});

const TrainingTabber = observer(({ store, sdk }: { store: any; sdk: any }) => {
    return (
        <TabView
            activeIndex={store.activeTab || 0}
            onTabChange={e => {
                store.onChangeTab?.(e.index);
            }}
        >
            <TabPanel header="Fragen">
                <TrainingQuestionCatalog
                    sdk={sdk}
                    store={store}
                />
            </TabPanel>
            <TabPanel header="Reinigungskräfte">
                <TrainingEmployeeResults
                    sdk={sdk}
                    store={store}
                />
            </TabPanel>
        </TabView>
    );
});

const TrainingDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog || null;

    return (
        <Dialog
            visible={!!store}
            header={store.header}
            onHide={() => {
                sdk.closeDialog();
            }}
            footer={() => (
                <DialogFooter
                    onSave={async () => {
                        store
                            .createOrUpdateTraining()
                            .then(({ id, event }: { id: number | null; event: any }) => {
                                if (event === 'NEW') {
                                    sdk.showSuccessToast('Schulung erfolgreich erstellt');
                                } else if (event === 'UPDATE') {
                                    sdk.showSuccessToast('Schulung erfolgreich aktualisiert');
                                    sdk.closeDialog(event);
                                }
                            })
                            .catch((error: any) => {
                                sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
                            });
                    }}
                    store={store}
                />
            )}
            style={{ width: store.isNewTraining ? '40rem' : '75rem' }}
        >
            {store.isLoading ? (
                <div
                    className="flex justify-content-center align-items-center"
                    style={{ height: '100%' }}
                >
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="dialog-content">
                    <div className={'left-column'}>
                        <div className={'card'}>
                            <UploadImageDialog store={store} />
                            {!store.isNewTraining && (
                                <div
                                    id="training-active"
                                    // className={`field-checkbox ${store.allQuestions?.length === 0 ? 'p-invalid' : ''}`}
                                    className={`field-checkbox`}
                                    style={{
                                        marginTop: '1rem',
                                        // border: store.allQuestions?.length === 0 ? '1px solid red' : 'none',
                                        border: 'none',
                                        padding: '0.5rem',
                                        borderRadius: '4px',
                                    }}
                                    data-pr-tooltip={
                                        'Aktiviere das Training - auch ohne Frage'
                                        // store.allQuestions?.length === 0
                                        //     ? 'Es muss mindestens eine Frage vorhanden sein, um die Schulung zu aktivieren.'
                                        //     : ''
                                    }
                                    data-pr-position="top"
                                >
                                    <Checkbox
                                        inputId="trainingActive"
                                        checked={store.isActive}
                                        // disabled={store.allQuestions?.length === 0}
                                        onChange={e => store.toggleActive()}
                                    />
                                    <label
                                        htmlFor="trainingActive"
                                        style={{ marginLeft: '0.5rem' }}
                                    >
                                        Schulung aktiv
                                    </label>
                                    <Tooltip target="#training-active" />
                                </div>
                            )}
                            <TrainingName store={store} />
                            <TrainingContent store={store} />
                            <TrainingDescription store={store} />
                        </div>
                    </div>
                    {!store.isNewTraining && (
                        <div className={'right-column'}>
                            <div className={'card'}>
                                <TrainingTabber
                                    sdk={sdk}
                                    store={store}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
});

export default TrainingDialog;
